import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileCheck, LayoutGrid, List } from 'lucide-react';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import {
  getAuditApprovals,
  AmsAuditApproval,
} from '../../../services/amsAuditApprovalService';
import { AmsProjectAudit } from '../../../services/amsProjectAuditService';
import { groupApprovalsByProject, deriveProjectApprovalStats } from '../utils/groupApprovalsByProject';
import AmsProjectApprovalSummaryCards from './AmsProjectApprovalSummaryCards';
import AmsProjectApprovalCard from './AmsProjectApprovalCard';

interface AmsAuditApprovalListProps {
  onAuditClick: (approval: AmsAuditApproval, audit: AmsProjectAudit | null) => void;
  refreshSignal?: number;
}

const ProjectCardSkeleton: React.FC = () => (
  <div className="h-[220px] animate-pulse rounded-2xl border border-gray-200 bg-white p-4">
    <div className="flex items-start justify-between">
      <div className="h-4 w-24 rounded bg-gray-200" />
      <div className="h-4 w-16 rounded-full bg-gray-200" />
    </div>
    <div className="mt-3 h-5 w-40 rounded bg-gray-200" />
    <div className="mt-2 h-3 w-28 rounded bg-gray-100" />
    <div className="mt-5 h-10 w-full rounded bg-gray-100" />
  </div>
);

const Band: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <div className="my-3 flex items-center gap-2.5">
    <span className="text-[11.5px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
    <span className="rounded-full border border-gray-200 bg-white px-2 text-[11px] font-semibold text-gray-400">
      {count} projects
    </span>
    <span className="h-px flex-1 bg-gray-200" />
  </div>
);

const AmsAuditApprovalList: React.FC<AmsAuditApprovalListProps> = ({ onAuditClick, refreshSignal }) => {
  const [approvals, setApprovals] = useState<AmsAuditApproval[]>([]);
  const [audits, setAudits] = useState<Map<string, AmsProjectAudit>>(new Map());
  const [projects, setProjects] = useState<Map<string, any>>(new Map());
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending_review' | 'partially_approved' | 'approved_to_start' | 'rejected'
  >('all');
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

  const loadApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAuditApprovals({ department: 'AMS', page: 1, limit: 1000 });
      const allApprovals = response.docs || [];
      setApprovals(allApprovals);

      // Backend now returns each approval's audit + project inline, so build the
      // lookup maps directly — no per-audit / per-project fetches (was 1 + N + M calls).
      const auditMap = new Map<string, AmsProjectAudit>();
      const projectMap = new Map<string, any>();
      allApprovals.forEach((a: any) => {
        if (a.audit) auditMap.set(a.amsProjectAuditId, a.audit);
        if (a.project) projectMap.set(String(a.project._id || a.project.id), a.project);
      });
      setAudits(auditMap);
      setProjects(projectMap);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load audit approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals, refreshSignal]);

  // Status filter applies BEFORE grouping. Drafts are always dropped inside the grouper.
  const filteredApprovals = useMemo(
    () => approvals.filter((a) => statusFilter === 'all' || a.overallStatus === statusFilter),
    [approvals, statusFilter],
  );

  const grouped = useMemo(
    () => groupApprovalsByProject(filteredApprovals, audits, projects),
    [filteredApprovals, audits, projects],
  );
  // Stats reflect the full (unfiltered, non-draft) picture so the KPIs stay stable while filtering.
  const stats = useMemo(
    () => deriveProjectApprovalStats(groupApprovalsByProject(approvals, audits, projects)),
    [approvals, audits, projects],
  );

  const totalGroups = grouped.needsReview.length + grouped.noAction.length;

  // Cards = multi-column grid; List = single full-width column (same cards, denser stack).
  const gridClass =
    viewMode === 'cards'
      ? 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'
      : 'grid grid-cols-1 gap-3';

  return (
    <div className="space-y-4">
      <AmsProjectApprovalSummaryCards stats={stats} />

      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Statuses</option>
          <option value="pending_review">Pending Review</option>
          <option value="partially_approved">Partially Approved</option>
          <option value="approved_to_start">Approved to Start</option>
          <option value="rejected">Rejected</option>
        </select>

        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
          <button
            type="button"
            onClick={() => setViewMode('cards')}
            aria-pressed={viewMode === 'cards'}
            title="Card view"
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
              viewMode === 'cards' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Cards
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            title="List view"
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
              viewMode === 'list' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <List className="h-3.5 w-3.5" />
            List
          </button>
        </div>

        <Button
          icon="pi pi-refresh"
          label="Refresh"
          className="p-button-outlined p-button-sm"
          loading={loading}
          onClick={loadApprovals}
        />
      </div>

      {loading ? (
        <div className={gridClass}>
          {Array.from({ length: 6 }).map((_, i) => (
            <ProjectCardSkeleton key={i} />
          ))}
        </div>
      ) : totalGroups === 0 ? (
        <div className="py-16 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-gray-100">
            <FileCheck className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mb-1 text-sm font-semibold text-gray-900">No audit approvals found</h3>
          <p className="mx-auto max-w-sm text-xs text-gray-500">
            {statusFilter !== 'all'
              ? `No submitted audits with status "${statusFilter}".`
              : 'No submitted audit approvals available.'}
          </p>
        </div>
      ) : (
        <>
          {grouped.needsReview.length > 0 && (
            <>
              <Band label="Needs your review" count={grouped.needsReview.length} />
              <div className={gridClass}>
                {grouped.needsReview.map((g) => (
                  <AmsProjectApprovalCard key={g.projectId} group={g} onOpenAudit={onAuditClick} />
                ))}
              </div>
            </>
          )}
          {grouped.noAction.length > 0 && (
            <>
              <Band label="No action needed" count={grouped.noAction.length} />
              <div className={gridClass}>
                {grouped.noAction.map((g) => (
                  <AmsProjectApprovalCard key={g.projectId} group={g} onOpenAudit={onAuditClick} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AmsAuditApprovalList;
